import React, { useCallback, useEffect, useState } from 'react';
import { BellRing, Check, ChevronDown, ChevronUp, FileClock, Filter, RefreshCw, ShieldCheck } from 'lucide-react';
import Layout from '../components/Layout';
import api from '../services/api';

const PAGE_SIZE = 50;

const ACTION_LABELS = {
  'clinic.context_select': 'Troca de clínica ativa',
  'clinic_user.update': 'Acesso de colaborador atualizado',
  'clinic_user.invite': 'Colaborador convidado',
  'clinic_user.invitation_cancel': 'Convite cancelado',
  'patient.assignment_update': 'Atribuição de paciente atualizada',
  'patient.assignment_sync': 'Atribuição de paciente atualizada',
  'appointment.conflict_override_create': 'Agendamento criado apesar de conflito de horário',
  'appointment.conflict_override_update': 'Agendamento remarcado apesar de conflito de horário',
  BILLING_PAYMENT_DATE_CORRECTED: 'CORRIGIU DATA DE RECEBIMENTO',
  PRONTUARIO_EXPORT: 'Prontuário exportado',
};

const formatAction = (action) => ACTION_LABELS[action] || String(action || 'Ação registrada').replaceAll('_', ' ');

const ROLE_LABELS = {
  clinic_admin: 'Administrador da clínica', dentist: 'Dentista', secretary: 'Secretária',
  assistant: 'Auxiliar / ASB / TSB', financial: 'Financeiro', reception: 'Recepção',
};
const STATUS_LABELS = { active: 'Ativo', suspended: 'Suspenso', revoked: 'Revogado' };
const PATIENT_SCOPE_LABELS = { all: 'Todos os pacientes da clínica', assigned: 'Somente pacientes atribuídos' };
const AGENDA_SCOPE_LABELS = { all: 'Agenda completa da clínica', busy_only: 'Agenda geral com privacidade', own: 'Somente a própria agenda' };
const PERMISSION_MODULES = {
  dashboard: 'Dashboard', patients: 'Pacientes', appointments: 'Agenda', clinical_records: 'Prontuário',
  anamnese: 'Anamnese', images: 'Imagens', documents: 'Documentos clínicos', contracts: 'Contratos',
  whatsapp: 'WhatsApp', inventory: 'Estoque', billing: 'Financeiro', ai: 'Inteligência artificial',
  settings: 'Configurações', subscription: 'Assinatura', audit: 'Auditoria',
};
const PERMISSION_ACTIONS = {
  view: 'visualizar', create: 'cadastrar', update: 'editar', inactivate: 'inativar', export: 'exportar',
  view_sensitive: 'visualizar dados sensíveis', update_sensitive: 'editar dados sensíveis', cancel: 'cancelar',
  delete: 'excluir', send_confirmation: 'enviar confirmações', sign: 'assinar', manage: 'gerenciar',
  send: 'enviar', upload: 'enviar arquivos', download: 'baixar arquivos', manage_templates: 'gerenciar modelos',
  view_costs: 'visualizar custos', receive_payment: 'registrar recebimentos', view_reports: 'visualizar relatórios',
  manage_expenses: 'gerenciar despesas', import_nfse: 'importar NFS-e', clinical_use: 'usar IA clínica',
  administrative_use: 'usar IA administrativa', financial_use: 'usar IA financeira',
  update_clinic: 'editar dados da clínica', manage_professionals: 'gerenciar profissionais',
  manage_integrations: 'gerenciar integrações', manage_users: 'gerenciar usuários',
  correct_payment_date: 'corrigir data de recebimento',
  manage_permissions: 'gerenciar permissões',
};

const formatPermissions = (permissions = []) => {
  const grouped = {};
  permissions.forEach((permission) => {
    const [module, ...actionParts] = String(permission).split('.');
    const moduleLabel = PERMISSION_MODULES[module] || module;
    const action = actionParts.join('_');
    if (!grouped[moduleLabel]) grouped[moduleLabel] = [];
    grouped[moduleLabel].push(PERMISSION_ACTIONS[action] || action.replaceAll('_', ' '));
  });
  return Object.entries(grouped).map(([module, actions]) => `${module}: ${actions.join(', ')}`).join('; ');
};

const difference = (next = [], previous = []) => next.filter((item) => !previous.includes(item));

const EXPORT_PURPOSES = {
  uso_interno_auditoria: 'Uso interno / auditoria', copia_paciente: 'Cópia solicitada pelo paciente',
  solicitacao_paciente: 'Cópia solicitada pelo paciente', copia_responsavel: 'Cópia solicitada por responsável',
  solicitacao_responsavel_representante: 'Cópia solicitada por responsável/representante',
  encaminhamento: 'Encaminhamento a outro profissional', encaminhamento_profissional: 'Encaminhamento a outro profissional',
  solicitacao_judicial: 'Solicitação judicial', outro: 'Outra finalidade',
};
const SOURCE_LABELS = {
  appointment: 'Agenda', prescriptions_documents: 'Documentos clínicos',
  client: 'Aplicação', server: 'Sistema',
};

const humanDetails = (item) => {
  const details = item.details || {};
  const lines = [];
  const previousOverrides = details.previous_permission_overrides || { allow: [], deny: [] };
  const nextOverrides = details.permission_overrides;

  if (nextOverrides) {
    const allowed = difference(nextOverrides.allow || [], previousOverrides.allow || []);
    const noLongerAllowed = difference(previousOverrides.allow || [], nextOverrides.allow || []);
    const denied = difference(nextOverrides.deny || [], previousOverrides.deny || []);
    const noLongerDenied = difference(previousOverrides.deny || [], nextOverrides.deny || []);
    if (allowed.length) lines.push({ label: 'Permissões liberadas', value: formatPermissions(allowed) });
    if (denied.length) lines.push({ label: 'Permissões bloqueadas', value: formatPermissions(denied) });
    if (noLongerAllowed.length) lines.push({ label: 'Permissões restauradas ao padrão', value: formatPermissions(noLongerAllowed) });
    if (noLongerDenied.length) lines.push({ label: 'Bloqueios removidos', value: formatPermissions(noLongerDenied) });
    if (!allowed.length && !denied.length && !noLongerAllowed.length && !noLongerDenied.length) {
      lines.push({ label: 'Permissões', value: 'Mantidas conforme o perfil de acesso' });
    }
  }
  if (details.role_id) lines.push({ label: 'Novo perfil', value: ROLE_LABELS[details.role_id] || details.role_id });
  if (details.status) lines.push({ label: 'Situação do acesso', value: STATUS_LABELS[details.status] || details.status });
  if (details.patient_scope) lines.push({ label: 'Visualização de pacientes', value: PATIENT_SCOPE_LABELS[details.patient_scope] || details.patient_scope });
  if (details.agenda_scope) lines.push({ label: 'Visualização da agenda', value: AGENDA_SCOPE_LABELS[details.agenda_scope] || details.agenda_scope });
  if (Object.prototype.hasOwnProperty.call(details, 'professional_id')) lines.push({ label: 'Profissional vinculado', value: details.professional_id ? 'Vínculo atualizado' : 'Vínculo removido' });
  if (details.email) lines.push({ label: 'E-mail', value: details.email });
  if (details.primary_professional_id) lines.push({ label: 'Dentista principal', value: 'Atribuição atualizada' });
  if (Array.isArray(details.assigned_professional_ids)) lines.push({ label: 'Dentistas com acesso compartilhado', value: `${details.assigned_professional_ids.length} profissional(is)` });
  if (details.previous_clinic_id || details.selected_clinic_id) lines.push({ label: 'Clínica ativa', value: 'Contexto de trabalho alterado' });
  if (details.source) lines.push({ label: 'Origem', value: SOURCE_LABELS[details.source] || 'Atualização automática do sistema' });
  if (details.title) lines.push({ label: 'Documento', value: details.title });
  if (details.file_name || details.filename) lines.push({ label: 'Arquivo', value: details.file_name || details.filename });
  if (details.to) lines.push({ label: 'Destinatário', value: details.to });
  if (details.finalidade) lines.push({ label: 'Finalidade', value: EXPORT_PURPOSES[details.finalidade] || details.finalidade });
  if (details.solicitante) lines.push({ label: 'Solicitante', value: details.solicitante });
  if (details.observacoes) lines.push({ label: 'Observações', value: details.observacoes });
  if (details.template_name) lines.push({ label: 'Modelo utilizado', value: details.template_name });
  if (details.fallback === 'reduced_pdf_quality') lines.push({ label: 'Envio', value: 'PDF otimizado para envio por e-mail' });
  if (details.reason) lines.push({ label: 'Motivo', value: details.reason });
  if (details.appointment_date) lines.push({ label: 'Data do agendamento', value: String(details.appointment_date).split('-').reverse().join('/') });
  if (details.start_time || details.end_time) lines.push({ label: 'Horário', value: `${details.start_time || '—'} às ${details.end_time || '—'}` });
  if (details.conflict_count) lines.push({ label: 'Conflitos confirmados', value: `${details.conflict_count} agendamento(s)` });
  return lines;
};

const formatDate = (value) => {
  if (!value) return 'Data não informada';

  let normalizedValue = String(value);
  if (/^\d{4}-\d{2}-\d{2}T/.test(normalizedValue) && !/[zZ]|[+-]\d{2}:\d{2}$/.test(normalizedValue)) {
    normalizedValue = `${normalizedValue}Z`;
  }

  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) return 'Data não informada';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'medium',
    timeZone: 'America/Recife',
  }).format(date);
};

const initialFilters = { action: '', actor_user_id: '', patient_query: '', date_from: '', date_to: '' };

export default function AuditPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [options, setOptions] = useState({ actors: [], actions: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [alertCount, setAlertCount] = useState(0);

  const loadOptions = useCallback(async () => {
    const response = await api.get('/audit/clinic/options');
    setOptions(response.data || { actors: [], actions: [] });
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (!value) return;
        if (key === 'date_from') params.date_from = new Date(`${value}T00:00:00`).toISOString();
        else if (key === 'date_to') params.date_to = new Date(`${value}T23:59:59.999`).toISOString();
        else params[key] = value;
      });
      const response = await api.get('/audit/clinic', { params });
      setItems(response.data?.items || []);
      setTotal(response.data?.total || 0);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || 'Não foi possível carregar a auditoria da clínica.');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page]);

  const loadAlerts = useCallback(async () => {
    const response = await api.get('/audit/alerts');
    setAlerts(response.data?.items || []);
    setAlertCount(response.data?.unread || 0);
  }, []);

  const acknowledgeAlert = async (alertId) => {
    await api.post(`/audit/alerts/${alertId}/acknowledge`);
    await loadAlerts();
  };

  useEffect(() => {
    loadOptions().catch(() => setOptions({ actors: [], actions: [] }));
  }, [loadOptions]);

  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => { loadAlerts().catch(() => { setAlerts([]); setAlertCount(0); }); }, [loadAlerts]);

  const applyFilters = (event) => {
    event.preventDefault();
    setPage(0);
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setPage(0);
  };

  const lastItem = Math.min((page + 1) * PAGE_SIZE, total);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-purple-600" /> Auditoria da clínica
            </h1>
            <p className="text-sm text-gray-500 mt-1">Consulte quem realizou cada ação e em qual contexto.</p>
          </div>
          <button type="button" onClick={loadEvents} disabled={loading} className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-60">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Atualizar
          </button>
        </div>

        {alerts.length > 0 && <section className="mb-6 rounded-xl border border-amber-200 bg-amber-50 overflow-hidden">
          <div className="px-5 py-4 border-b border-amber-200 flex items-center gap-2">
            <BellRing className="w-5 h-5 text-amber-700" />
            <h2 className="font-semibold text-amber-950">Exportações de dados para revisar</h2>
            <span className="ml-auto text-sm text-amber-800">{alertCount} {alertCount === 1 ? 'pendente' : 'pendentes'}</span>
          </div>
          <div className="divide-y divide-amber-200">
            {alerts.map((alert) => {
              const actor = alert.actor?.full_name || alert.actor?.email || 'Usuário da clínica';
              const patient = alert.patient?.full_name || alert.patient?.name || 'Paciente não localizado';
              return <div key={alert.id} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-950">Prontuário de {patient} exportado por {actor}</p>
                  <p className="text-xs text-amber-800 mt-1">{formatDate(alert.created_at)} · Revise o evento abaixo se a exportação não era esperada.</p>
                </div>
                <button type="button" onClick={() => acknowledgeAlert(alert.id)} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-amber-900 hover:bg-amber-100">
                  <Check className="w-4 h-4" /> Marcar como revisado
                </button>
              </div>;
            })}
          </div>
        </section>}

        <form onSubmit={applyFilters} className="bg-white border rounded-xl shadow-sm p-5 mb-6">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2 mb-4"><Filter className="w-4 h-4 text-purple-600" /> Filtros</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Colaborador</label>
              <select value={filters.actor_user_id} onChange={(e) => setFilters({ ...filters, actor_user_id: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">Todos</option>
                {options.actors.map((actor) => <option key={actor.id} value={actor.id}>{actor.full_name || actor.email}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Ação</label>
              <select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">Todas</option>
                {options.actions.map((action) => <option key={action} value={action}>{formatAction(action)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Paciente</label>
              <input value={filters.patient_query} onChange={(e) => setFilters({ ...filters, patient_query: e.target.value })} placeholder="Nome, CPF ou e-mail" className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Data inicial</label>
              <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Data final</label>
              <input type="date" min={filters.date_from || undefined} value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="flex flex-wrap justify-end gap-2 mt-4">
            <button type="button" onClick={clearFilters} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Limpar</button>
            <button type="submit" className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">Aplicar filtros</button>
          </div>
        </form>

        {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b flex items-center justify-between gap-3">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2"><FileClock className="w-4 h-4 text-purple-600" /> Eventos registrados</h2>
            <span className="text-sm text-gray-500">{total} {total === 1 ? 'evento' : 'eventos'}</span>
          </div>
          {loading ? <div className="p-10 text-center text-gray-500">Carregando auditoria...</div> : items.length === 0 ? <div className="p-10 text-center text-gray-500">Nenhum evento encontrado para os filtros selecionados.</div> : (
            <div className="divide-y">
              {items.map((item, index) => {
                const eventKey = `${item.id || 'legacy'}-${item.created_at || index}-${index}`;
                const expanded = expandedId === eventKey;
                const actorName = item.actor?.full_name || item.actor?.email || (item.actor_user_id ? 'Usuário não localizado' : 'Ação automática do sistema');
                const targetName = item.target_user?.full_name || item.target_user?.email;
                const patientName = item.patient?.full_name || item.patient?.name;
                const readableDetails = humanDetails(item);
                const hasDetails = readableDetails.length > 0;
                return <div key={eventKey} className="p-5">
                  <div className="flex flex-col md:flex-row md:items-start gap-3">
                    <div className="flex-1 min-w-0 text-left">
                      <p className="font-medium text-gray-900">{formatAction(item.action)}</p>
                      <p className="text-sm text-gray-600 mt-1">{actorName}{targetName ? ` · Colaborador: ${targetName}` : ''}{patientName ? ` · Paciente: ${patientName}` : ''}</p>
                      <p className="text-xs text-gray-400 mt-1">{formatDate(item.created_at)} · {item.outcome === 'success' || !item.outcome ? 'Concluído' : item.outcome}</p>
                    </div>
                    {hasDetails && <button type="button" onClick={() => setExpandedId(expanded ? null : eventKey)} className="inline-flex items-center gap-1 text-sm text-purple-700 hover:text-purple-800">
                      {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />} {expanded ? 'Ocultar detalhes' : 'Ver detalhes'}
                    </button>}
                  </div>
                  {expanded && <div className="mt-4 rounded-lg bg-gray-50 border px-4 py-3 text-left">
                    <dl className="space-y-2">{readableDetails.map((detail, detailIndex) => <div key={`${detail.label}-${detailIndex}`} className="text-sm sm:flex sm:gap-2">
                      <dt className="font-medium text-gray-700 sm:min-w-52">{detail.label}:</dt>
                      <dd className="text-gray-600">{detail.value}</dd>
                    </div>)}</dl>
                  </div>}
                </div>;
              })}
            </div>
          )}
          {total > PAGE_SIZE && <div className="px-5 py-4 border-t flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
            <span className="text-gray-500">Exibindo {page * PAGE_SIZE + 1}–{lastItem} de {total}</span>
            <div className="flex gap-2">
              <button disabled={page === 0 || loading} onClick={() => setPage((value) => Math.max(0, value - 1))} className="px-3 py-2 border rounded-lg disabled:opacity-40">Anterior</button>
              <button disabled={lastItem >= total || loading} onClick={() => setPage((value) => value + 1)} className="px-3 py-2 border rounded-lg disabled:opacity-40">Próxima</button>
            </div>
          </div>}
        </div>
      </div>
    </Layout>
  );
}
